/**
 * 天枢超级底座 - 前端 API 客户端
 * 统一封装与 Java 后端的 HTTP 通信
 */

const BASE_URL = '/api';

interface ApiResponse<T = unknown> {
  code: number;
  msg: string;
  message?: string; // 兼容旧版 mock 路由
  data: T;
}

interface PageData<T> {
  records: T[];
  total: number;
  pageNum: number;
  pageSize: number;
  totalPages: number;
}

interface PageParams {
  pageNum?: number;
  pageSize?: number;
  [key: string]: unknown;
}

/**
 * 通用请求方法
 */
async function request<T>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('ts_token') : null;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = token;
  }

  const response = await fetch(`${BASE_URL}${url}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    // Token 过期，跳转登录
    if (typeof window !== 'undefined') {
      localStorage.removeItem('ts_token');
      window.location.href = '/login';
    }
    throw new Error('未授权，请重新登录');
  }

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

// ========== 认证 ==========

export const authApi = {
  login: (tenantId: string, username: string, password: string) =>
    request<{ token: string; user: Record<string, unknown>; tenant?: Record<string, unknown> }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ tenantId, username, password }),
    }),

  logout: () =>
    request<void>('/auth/logout', { method: 'POST' }),

  info: () =>
    request<{ user: Record<string, unknown>; tenant: Record<string, unknown> }>('/auth/info'),
};

// ========== 租户管理 ==========

// 前端字段 → 后端Entity字段映射
function toBackend(data: Record<string, unknown>): Record<string, unknown> {
  const mapped: Record<string, unknown> = { ...data };
  if ("tenantCode" in mapped) { mapped.code = mapped.tenantCode; delete mapped.tenantCode; }
  if ("contactName" in mapped) { mapped.contactPerson = mapped.contactName; delete mapped.contactName; }
  if ("contactEmail" in mapped) { mapped.email = mapped.contactEmail; delete mapped.contactEmail; }
  if ("plan" in mapped) { mapped.planId = mapped.plan; delete mapped.plan; }
  if ("model" in mapped) { mapped.modelType = mapped.model; delete mapped.model; }
  if ("phone" in mapped) { mapped.contactPhone = mapped.phone; delete mapped.phone; }
  return mapped;
}

// 后端Entity字段 → 前端字段映射
function toFrontend(data: Record<string, unknown>): Record<string, unknown> {
  const mapped: Record<string, unknown> = { ...data };
  if ("code" in mapped) { mapped.tenantCode = mapped.code; }
  if ("contactPerson" in mapped) { mapped.contactName = mapped.contactPerson; }
  if ("email" in mapped) { mapped.contactEmail = mapped.email; }
  if ("planId" in mapped) { mapped.plan = mapped.planId; }
  return mapped;
}

export const tenantApi = {
  page: (params: { pageNum?: number; pageSize?: number; keyword?: string; modelType?: string; status?: string }) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<Record<string, unknown>>>(`/governance/tenant/page?${query}`);
  },

  getById: (id: string) =>
    request<Record<string, unknown>>(`/governance/tenant/${id}`),

  create: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/governance/tenant', {
      method: 'POST',
      body: JSON.stringify(toBackend(data)),
    }),

  update: (id: string, data: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/governance/tenant/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(toBackend(data)),
    }),

  delete: (id: string) =>
    request<void>(`/governance/tenant/${id}`, { method: 'DELETE' }),

  updateStatus: (id: string, status: string) =>
    request<void>(`/governance/tenant/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  stats: () =>
    request<Record<string, number>>('/governance/tenant/stats'),
};

// ========== 审计日志 ==========

export const auditApi = {
  page: (params: { pageNum?: number; pageSize?: number; tenantId?: string; module?: string; action?: string; status?: string }) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<Record<string, unknown>>>(`/audit/logs/page?${query}`);
  },
};

// ========== 安全事件 ==========

export const securityApi = {
  page: (params: { pageNum?: number; pageSize?: number; severity?: string; status?: string; eventType?: string }) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<Record<string, unknown>>>(`/security/events/page?${query}`);
  },

  getById: (id: string) =>
    request<Record<string, unknown>>(`/security/events/${id}`),

  create: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/security/events', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateStatus: (id: string, status: string) =>
    request<void>(`/security/events/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  stats: () =>
    request<Record<string, number>>('/security/events/stats'),
};

// ========== 邮件域名 ==========

export const mailApi = {
  domains: {
    page: (params: { pageNum?: number; pageSize?: number; tenantId?: string; status?: string }) => {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
      return request<PageData<Record<string, unknown>>>(`/mail/domains/page?${query}`);
    },

    create: (data: Record<string, unknown>) =>
      request<Record<string, unknown>>('/mail/domains', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/mail/domains/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      request<void>(`/mail/domains/${id}`, { method: 'DELETE' }),
  },
};

// ========== 文档管理 ==========

export const docsApi = {
  files: {
    page: (params: { pageNum?: number; pageSize?: number; spaceId?: string; folderId?: string; type?: string; keyword?: string }) => {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
      return request<PageData<Record<string, unknown>>>(`/docs/files/page?${query}`);
    },

    create: (data: Record<string, unknown>) =>
      request<Record<string, unknown>>('/docs/files', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/docs/files/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      request<void>(`/docs/files/${id}`, { method: 'DELETE' }),

    toggleStar: (id: string) =>
      request<void>(`/docs/files/${id}/star`, { method: 'PATCH' }),
  },
};

// ========== 业务中台 ==========

export const middleApi = {
  integrations: {
    page: (params: { pageNum?: number; pageSize?: number; keyword?: string; status?: string; mode?: string }) => {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
      return request<PageData<Record<string, unknown>>>(`/B/integrations/page?${query}`);
    },

    create: (data: Record<string, unknown>) =>
      request<Record<string, unknown>>('/B/integrations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Record<string, unknown>) =>
      request<Record<string, unknown>>(`/B/integrations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      request<void>(`/B/integrations/${id}`, { method: 'DELETE' }),
  },
};


// ========== 权限管理 ==========

export const permissionApi = {
  list: () => request<Record<string, unknown>[]>('/auth/permissions'),
};

// ========== 域名管理 ==========

export const domainApi = {
  page: (params?: PageParams) => {
    const query = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<unknown>>(`/domain/domains/page?${query}`);
  },
  certs: (params?: PageParams) => {
    const query = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<unknown>>(`/domain/certs/page?${query}`);
  },
  routes: (params?: PageParams) => {
    const query = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<unknown>>(`/domain/routes/page?${query}`);
  },
};

// ========== 统一认证中心 ==========

export const authCenterApi = {
  identitySources: (params?: PageParams) => {
    const query = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<unknown>>(`/auth-center/identity-sources/page?${query}`);
  },
  authPolicies: (params?: PageParams) => {
    const query = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<unknown>>(`/auth-center/auth-policies/page?${query}`);
  },
  ssoApps: (params?: PageParams) => {
    const query = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<unknown>>(`/auth-center/sso-apps/page?${query}`);
  },
};

// ========== 灵钛开放平台 ==========

export const openPlatformApi = {
  apps: (params?: PageParams) => {
    const query = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<unknown>>(`/E/apps/page?${query}`);
  },
};

// ========== 灵耀分控台 ==========

export const lingyaoApi = {
  subscription: (params?: PageParams) => {
    const query = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<unknown>>(`/F/subscription/page?${query}`);
  },

  // === 租户概览 ===
  overview: (tenantId: string) =>
    request<Record<string, unknown>>(`/lingyao/overview?tenantId=${tenantId}`),

  // === 团队管理 - 部门 ===
  deptTree: (tenantId: string) =>
    request<Record<string, unknown>[]>(`/lingyao/team/dept/tree?tenantId=${tenantId}`),
  createDept: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>("/lingyao/team/dept", { method: "POST", body: JSON.stringify(data) }),
  updateDept: (id: string, data: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/lingyao/team/dept/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteDept: (id: string) =>
    request<void>(`/lingyao/team/dept/${id}`, { method: "DELETE" }),

  // === 团队管理 - 用户 ===
  teamUserList: (params: { tenantId: string; deptId?: string; keyword?: string; roleId?: string; pageNum?: number; pageSize?: number }) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<Record<string, unknown>>(`/lingyao/team/user/list?${query}`);
  },
  teamUserDetail: (id: string) =>
    request<Record<string, unknown>>(`/lingyao/team/user/${id}`),
  createTeamUser: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>("/lingyao/team/user", { method: "POST", body: JSON.stringify(data) }),
  updateTeamUser: (id: string, data: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/lingyao/team/user/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  assignUserRoles: (id: string, roleIds: string[]) =>
    request<void>(`/lingyao/team/user/${id}/roles`, { method: "PUT", body: JSON.stringify({ roleIds }) }),
  updateUserStatus: (id: string, status: string) =>
    request<void>(`/lingyao/team/user/${id}/status`, { method: "PUT", body: JSON.stringify({ status }) }),

  // === 权限中心 - 角色 ===
  roleList: (params: { tenantId: string; pageNum?: number; pageSize?: number; keyword?: string }) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<Record<string, unknown>>(`/lingyao/permission/role/list?${query}`);
  },
  allRoles: (tenantId: string) =>
    request<Record<string, unknown>[]>(`/lingyao/permission/role/all?tenantId=${tenantId}`),
  createRole: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>("/lingyao/permission/role", { method: "POST", body: JSON.stringify(data) }),
  updateRole: (id: string, data: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/lingyao/permission/role/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteRole: (id: string) =>
    request<void>(`/lingyao/permission/role/${id}`, { method: "DELETE" }),

  // === 权限中心 - 权限 ===
  permTree: () =>
    request<Record<string, unknown>[]>("/lingyao/permission/perm/tree"),
  rolePerms: (id: string) =>
    request<Record<string, unknown>>(`/lingyao/permission/role/${id}/perms`),
  saveRolePerms: (id: string, permIds: string[]) =>
    request<void>(`/lingyao/permission/role/${id}/perms`, { method: "PUT", body: JSON.stringify({ permIds }) }),
};


// ========== 基础资料 ==========

export const employeeApi = {
  page: (params?: PageParams) => {
    const query = new URLSearchParams();
    if (params) Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<unknown>>(`/L/employee/page?${query}`);
  },
  create: (data: Record<string, unknown>) => request('/L/employee/page', { method: 'POST', body: JSON.stringify(data) }),
};

// ========== 通知消息 ==========

export const notificationApi = {
  page: (params: { pageNum?: number; pageSize?: number; category?: string; status?: string }) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<Record<string, unknown>>>(`/notification/messages/page?${query}`);
  },
};

export type { ApiResponse, PageData };

// ========== 用户管理 API ==========
export const userApi = {
  getTenantAdmin: (tenantId: string) =>
    request<{ exists: boolean; id?: string; username?: string; displayName?: string; email?: string; phone?: string; status?: string; hasPassword?: boolean; isSuperUser?: boolean; lastLoginAt?: string }>(`/governance/user/tenant/${tenantId}/admin`),

  sendSmsCode: (tenantId: string) =>
    request<{ phone: string; message: string }>(`/governance/user/tenant/${tenantId}/send-sms-code`, {
      method: 'POST',
    }),

  resetPassword: (tenantId: string, newPassword: string, smsCode: string) =>
    request<void>(`/governance/user/tenant/${tenantId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword, smsCode }),
    }),
};

// ========== 角色管理 API ==========
export const roleApi = {
  page: (params: { tenantId?: string; pageNum?: number; pageSize?: number; keyword?: string }) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return request<PageData<Record<string, unknown>>>(`/governance/role/page?${query}`);
  },

  listByTenant: (tenantId: string) =>
    request<Record<string, unknown>>(`/governance/role/list?tenantId=${tenantId}`),

  getById: (id: string) =>
    request<Record<string, unknown>>(`/governance/role/${id}`),

  create: (data: Record<string, unknown>) =>
    request<Record<string, unknown>>('/governance/role', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/governance/role/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    request<void>(`/governance/role/${id}`, { method: 'DELETE' }),
};

// ========== 菜单管理 ==========
export const menuApi = {
  tree: (bizNav?: string) => {
    const query = bizNav ? `?bizNav=${bizNav}` : '';
    return request<Record<string, unknown>[]>(`/governance/menu/tree${query}`);
  },
  list: () => request<Record<string, unknown>[]>('/governance/menu/list'),
};
